import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchCasaMapaMarketTrends } from "../../../api/clientCasamapaAdminApi";
import CasaMapaAdminFilterCombobox from "../../../components/casamapa/admin/CasaMapaAdminFilterCombobox";
import {
  PriceHistoryChart,
  PropertyPriceSparkline,
  WeeklyMovementChart,
} from "../../../components/casamapa/admin/CasaMapaMarketTrendCharts";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useCurrency, useLocale } from "../../../intl";
import "../../../styles/casamapaAdminPortal.css";

const COPY = "realEstate.casaMapa.admin.marketTrends";
const WINDOWS = [30, 90, 180];
const TYPES = ["ALL", "SALE", "RENT"];

function placeLabel(row) {
  return [row.neighborhood, row.city].filter(Boolean).join(", ") || "—";
}

function isActualPriceChange(row) {
  return row?.previousPrice != null && row?.newPrice != null && Number(row.previousPrice) !== Number(row.newPrice);
}

function compareSortValues(left, right, type) {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (type === "number") return Number(left) - Number(right);
  if (type === "date") return new Date(left).getTime() - new Date(right).getTime();
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
}

function sortRows(rows, sort) {
  if (!sort?.key) return rows;
  const direction = sort.dir === "desc" ? -1 : 1;
  return [...rows].sort((left, right) => {
    const leftValue = sort.get(left);
    const rightValue = sort.get(right);
    if (leftValue == null && rightValue == null) return 0;
    if (leftValue == null) return 1;
    if (rightValue == null) return -1;
    return compareSortValues(leftValue, rightValue, sort.type) * direction;
  });
}

function nextSort(current, column) {
  if (current.key === column.key) {
    return { ...column, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { ...column, dir: column.type === "string" ? "asc" : "desc" };
}

function CollapsiblePanel({ title, hint, children, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const titleId = `${String(title || "panel").replace(/\s+/g, "-").toLowerCase()}-title`;

  return (
    <section className="panel-card casamapa-admin-section cm-admin-collapse">
      <button
        type="button"
        className="casamapa-admin-section__header cm-admin-collapse__summary"
        aria-expanded={isOpen}
        aria-controls={`${titleId}-body`}
        onClick={() => setIsOpen((current) => !current)}
      >
        <div>
          <h2 id={titleId}>{title}</h2>
          {hint ? <p className="muted">{hint}</p> : null}
        </div>
        <span className="cm-admin-collapse__chevron" aria-hidden="true" />
      </button>
      {isOpen ? (
        <div className="cm-admin-collapse__body" id={`${titleId}-body`}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

function SortableHeader({ column, sort, onSort, t }) {
  const active = sort.key === column.key;
  const ariaSort = active ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
  return (
    <th aria-sort={ariaSort}>
      <button
        type="button"
        className={`cm-admin-sort${active ? " is-active" : ""}`}
        onClick={() => onSort(nextSort(sort, column))}
        aria-label={`${t(`${COPY}.sortColumn`, { column: column.label })}${
          active
            ? `, ${t(sort.dir === "asc" ? `${COPY}.sortAscending` : `${COPY}.sortDescending`)}`
            : ""
        }`}
      >
        {column.label}
        <span className="cm-admin-sort__icon" aria-hidden="true">
          {active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

function TrendArrow({ delta }) {
  const direction = delta?.direction || "flat";
  const symbol = direction === "up" ? "↑" : direction === "down" ? "↓" : "→";
  let label = "—";
  if (delta?.percent != null && Math.abs(delta.percent) <= 400) {
    label = `${delta.percent > 0 ? "+" : ""}${delta.percent}%`;
  } else if (delta?.change) {
    label = `${delta.change > 0 ? "+" : ""}${delta.change.toLocaleString()}`;
  }
  return (
    <small className={`cm-trend-delta cm-trend-delta--${direction}`}>
      <span aria-hidden="true">{symbol}</span> {label}
    </small>
  );
}

function Metric({ label, value, hint, delta, tone }) {
  return (
    <article className={`cm-admin-analytics__metric${tone ? ` cm-admin-analytics__metric--${tone}` : ""}`}>
      <span className="cm-admin-analytics__metric-label">{label}</span>
      <strong className="cm-admin-analytics__metric-value">{value}</strong>
      {delta ? <TrendArrow delta={delta} /> : null}
      {hint ? <small className="cm-admin-analytics__metric-hint">{hint}</small> : null}
    </article>
  );
}

function Fact({ label, children }) {
  const empty = children == null || children === "";
  return (
    <div className="cm-trend-modal__fact">
      <dt>{label}</dt>
      <dd>{empty ? "—" : children}</dd>
    </div>
  );
}

function PropertyTrendModal({
  property,
  points,
  loading,
  onClose,
  t,
  typeLabel,
  money,
  dateLabel,
  formatMoney,
  adminPath,
  publicPath,
}) {
  const titleId = useId();
  const closeButtonRef = useRef(null);
  const historyPoints = (points || []).filter((point) => point.changeType !== "PREVIOUS");
  const pricedPoints = (points || []).filter((point) => point.price != null);
  const startPrice =
    pricedPoints[0]?.previousPrice ??
    points?.find((point) => point.previousPrice != null)?.previousPrice ??
    property.previousPrice;
  const endPrice = pricedPoints[pricedPoints.length - 1]?.price ?? property.newPrice ?? property.currentPrice;
  const chartPoints =
    pricedPoints.length >= 2
      ? points
      : startPrice != null && endPrice != null
        ? [
            {
              changedAt: property.changedAt,
              changeType: "PREVIOUS",
              previousPrice: null,
              price: startPrice,
              changePercent: null,
            },
            {
              changedAt: property.changedAt,
              changeType: property.changeType || "CURRENT",
              previousPrice: startPrice,
              price: endPrice,
              changePercent: property.changePercent,
            },
          ]
        : pricedPoints;
  const recordedPoints = historyPoints.length
    ? historyPoints
    : property.previousPrice != null || property.newPrice != null
      ? [
          {
            changedAt: property.changedAt,
            changeType: property.changeType,
            previousPrice: property.previousPrice,
            price: property.newPrice,
            changePercent: property.changePercent,
          },
        ]
      : [];

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const direction =
    property.changePercent > 0 || property.changeType === "PRICE_INCREASED"
      ? "up"
      : property.changePercent < 0 || property.changeType === "PRICE_DECREASED"
        ? "down"
        : "flat";

  const areaLabel =
    property.areaM2 != null ? `${Number(property.areaM2).toLocaleString(undefined, { maximumFractionDigits: 0 })} m²` : null;

  return createPortal(
    <div className="cm-trend-modal-root">
      <div className="cm-trend-modal-backdrop" role="presentation" onClick={onClose}>
        <div
          className="cm-trend-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(event) => event.stopPropagation()}
        >
        <header className="cm-trend-modal__header">
          <div className="cm-trend-modal__intro">
            {property.primaryImageUrl ? (
              <img className="cm-trend-modal__photo" src={property.primaryImageUrl} alt="" />
            ) : null}
            <div>
              <p className="cm-trend-modal__eyebrow">{t(`${COPY}.propertyModal.eyebrow`)}</p>
              <h2 id={titleId}>{property.title || t(`${COPY}.untitled`)}</h2>
              <p className="muted">{placeLabel(property)}</p>
              {property.canonicalListingId ? (
                <nav className="cm-trend-modal__links" aria-label={t(`${COPY}.propertyModal.linksAria`)}>
                  {publicPath ? (
                    <a href={publicPath} target="_blank" rel="noopener noreferrer">
                      {t(`${COPY}.propertyModal.viewPublic`)}
                    </a>
                  ) : null}
                  {adminPath ? (
                    <a href={adminPath} target="_blank" rel="noopener noreferrer">
                      {t(`${COPY}.propertyModal.viewAdmin`)}
                    </a>
                  ) : null}
                </nav>
              ) : null}
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="cm-trend-modal__close"
            onClick={onClose}
          >
            {t(`${COPY}.propertyModal.close`)}
          </button>
        </header>
        <div className="cm-trend-modal__body">
          <dl className="cm-trend-modal__facts">
            <Fact label={t(`${COPY}.when`)}>{dateLabel(property.changedAt)}</Fact>
            <Fact label={t(`${COPY}.listingType`)}>{typeLabel(property.listingType)}</Fact>
            <Fact label={t(`${COPY}.propertyModal.propertyType`)}>{property.propertyType}</Fact>
            <Fact label={t(`${COPY}.propertyModal.bedrooms`)}>{property.bedrooms}</Fact>
            <Fact label={t(`${COPY}.propertyModal.bathrooms`)}>{property.bathrooms}</Fact>
            <Fact label={t(`${COPY}.propertyModal.area`)}>{areaLabel}</Fact>
            <Fact label={t(`${COPY}.propertyModal.previousPrice`)}>{money(property.previousPrice)}</Fact>
            <Fact label={t(`${COPY}.propertyModal.newPrice`)}>{money(property.newPrice)}</Fact>
            <Fact label={t(`${COPY}.propertyModal.currentPrice`)}>{money(property.currentPrice)}</Fact>
            <Fact label={t(`${COPY}.change`)}>
              <span className={`cm-trend-delta cm-trend-delta--${direction}`}>
                {direction === "up" ? "↑" : direction === "down" ? "↓" : "→"}{" "}
                {property.changePercent != null
                  ? `${property.changePercent > 0 ? "+" : ""}${property.changePercent.toFixed(1)}%`
                  : "—"}
              </span>
            </Fact>
            <Fact label={t(`${COPY}.sources`)}>{property.activeSourceCount ?? "—"}</Fact>
          </dl>

          <section className="cm-trend-modal__section">
            <h3>{t(`${COPY}.propertyModal.chartTitle`)}</h3>
            <PropertyPriceSparkline points={chartPoints} t={t} formatMoney={formatMoney} />
            {loading ? <p className="muted">{t(`${COPY}.updating`)}</p> : null}
          </section>

          <section className="cm-trend-modal__section">
            <h3>{t(`${COPY}.propertyModal.historyTitle`)}</h3>
            {recordedPoints.length ? (
              <div className="casamapa-admin-table-wrap">
                <table className="casamapa-admin-table">
                  <thead>
                    <tr>
                      <th>{t(`${COPY}.when`)}</th>
                      <th>{t(`${COPY}.propertyModal.previousPrice`)}</th>
                      <th>{t(`${COPY}.propertyModal.newPrice`)}</th>
                      <th>{t(`${COPY}.change`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordedPoints.map((point, index) => {
                      const pointDirection =
                        point.changePercent > 0 || point.changeType === "PRICE_INCREASED"
                          ? "up"
                          : point.changePercent < 0 || point.changeType === "PRICE_DECREASED"
                            ? "down"
                            : "flat";
                      return (
                        <tr key={`${point.changedAt}-${point.changeType}-${index}`}>
                          <td>{dateLabel(point.changedAt)}</td>
                          <td>{money(point.previousPrice)}</td>
                          <td>{money(point.price)}</td>
                          <td>
                            <span className={`cm-trend-delta cm-trend-delta--${pointDirection}`}>
                              {pointDirection === "up" ? "↑" : pointDirection === "down" ? "↓" : "→"}{" "}
                              {point.changePercent != null
                                ? `${point.changePercent > 0 ? "+" : ""}${Number(point.changePercent).toFixed(1)}%`
                                : "—"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">{t(`${COPY}.emptyPropertyHistory`)}</p>
            )}
          </section>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function CasaMapaAdminMarketTrendsPage() {
  const { clientViewId, path } = useCasaMapaAdminPortal();
  const { t, locale } = useLocale();
  const { formatMoney } = useCurrency();
  const [days, setDays] = useState(90);
  const [listingType, setListingType] = useState("ALL");
  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [citySort, setCitySort] = useState({
    key: "properties",
    dir: "desc",
    type: "number",
    get: (row) => row.properties,
  });
  const [recentSort, setRecentSort] = useState({
    key: "when",
    dir: "desc",
    type: "date",
    get: (row) => row.changedAt,
  });

  const load = useCallback(async () => {
    if (!clientViewId) return;
    setLoading(true);
    setError("");
    try {
      setReport(
        await fetchCasaMapaMarketTrends(clientViewId, {
          days,
          listingType: listingType === "ALL" ? undefined : listingType,
          city: cities.length ? cities.join(",") : undefined,
        })
      );
    } catch (err) {
      setReport(null);
      setError(err.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [cities, clientViewId, days, listingType, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!clientViewId || !selectedId) {
      setHistoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setHistoryLoading(true);
    fetchCasaMapaMarketTrends(clientViewId, {
      days,
      listingType: listingType === "ALL" ? undefined : listingType,
      city: cities.length ? cities.join(",") : undefined,
      historyId: selectedId,
    })
      .then((next) => {
        if (cancelled) return;
        setReport((current) =>
          current
            ? { ...current, selectedPriceHistory: next.selectedPriceHistory }
            : next
        );
      })
      .catch(() => {
        if (!cancelled) {
          setReport((current) =>
            current
              ? { ...current, selectedPriceHistory: { canonicalListingId: selectedId, points: [] } }
              : current
          );
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cities, clientViewId, days, listingType, selectedId]);

  const count = (value) => (value == null ? "—" : Number(value).toLocaleString(locale));
  const money = (value) => (value == null ? "—" : formatMoney(value));
  const area = (value) =>
    value == null
      ? "—"
      : `${Number(value).toLocaleString(locale, { maximumFractionDigits: 0 })} m²`;
  const dateLabel = (value) =>
    value ? new Date(value).toLocaleDateString(locale, { month: "short", day: "numeric" }) : "—";
  const typeLabel = (value) => {
    const key = String(value || "").toUpperCase();
    if (key === "SALE") return t(`${COPY}.sale`);
    if (key === "RENT") return t(`${COPY}.rent`);
    return value || "—";
  };

  const selectedRow = useMemo(() => {
    if (!selectedId && !selectedProperty) return null;
    const fromGrid = selectedId
      ? report?.recentPriceChanges?.find((row) => row.canonicalListingId === selectedId)
      : null;
    const fromHistory = report?.selectedPriceHistory?.property;
    if (!fromGrid && !fromHistory && !selectedProperty) return null;
    return { ...fromHistory, ...selectedProperty, ...fromGrid, ...fromHistory };
  }, [report, selectedId, selectedProperty]);
  const closePropertyModal = useCallback(() => {
    setSelectedId("");
    setSelectedProperty(null);
  }, []);
  const openPropertyModal = useCallback((row) => {
    setSelectedProperty(row);
    setSelectedId(row.canonicalListingId);
  }, []);
  useEffect(() => {
    const next = report?.filterOptions?.cities || [];
    if (!next.length) return;
    setCityOptions((current) => {
      const merged = new Set([...current, ...next]);
      cities.forEach((name) => merged.add(name));
      return [...merged].sort((a, b) => a.localeCompare(b, locale));
    });
  }, [cities, locale, report]);

  const summary = report?.summary;
  const cityRows = useMemo(() => {
    const rows = report?.cities || [];
    const selected = new Set(cities);
    return sortRows(
      selected.size ? rows.filter((row) => selected.has(row.city)) : rows,
      citySort
    );
  }, [cities, citySort, report]);
  const recentRows = useMemo(() => {
    let rows = (report?.recentPriceChanges || []).filter(isActualPriceChange);
    if (cities.length) {
      const selected = new Set(cities);
      rows = rows.filter((row) => selected.has(row.city));
    }
    if (listingType !== "ALL") {
      rows = rows.filter((row) => String(row.listingType || "").toUpperCase() === listingType);
    }
    return sortRows(rows, recentSort);
  }, [cities, listingType, recentSort, report]);
  const cityColumns = useMemo(
    () => [
      { key: "city", label: t(`${COPY}.city`), type: "string", get: (row) => row.city },
      { key: "listingType", label: t(`${COPY}.listingType`), type: "string", get: (row) => row.listingType },
      { key: "properties", label: t(`${COPY}.properties`), type: "number", get: (row) => row.properties },
      { key: "medianPrice", label: t(`${COPY}.medianPrice`), type: "number", get: (row) => row.medianPrice },
      { key: "medianAreaM2", label: t(`${COPY}.medianAreaM2`), type: "number", get: (row) => row.medianAreaM2 },
      {
        key: "medianPricePerM2",
        label: t(`${COPY}.medianPricePerM2`),
        type: "number",
        get: (row) => row.medianPricePerM2,
      },
    ],
    [t]
  );
  const recentColumns = useMemo(
    () => [
      { key: "when", label: t(`${COPY}.when`), type: "date", get: (row) => row.changedAt },
      { key: "property", label: t(`${COPY}.property`), type: "string", get: (row) => row.title },
      { key: "change", label: t(`${COPY}.change`), type: "number", get: (row) => row.changePercent },
      { key: "price", label: t(`${COPY}.price`), type: "number", get: (row) => row.newPrice },
      { key: "sources", label: t(`${COPY}.sources`), type: "number", get: (row) => row.activeSourceCount },
    ],
    [t]
  );

  return (
    <div className="casamapa-admin-page">
      <header className="casamapa-admin-page__header">
        <div>
          <div className="eyebrow">{t(`${COPY}.eyebrow`)}</div>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="muted">{t(`${COPY}.subtitle`)}</p>
        </div>
      </header>

      <form className="cm-trend-filters" onSubmit={(event) => event.preventDefault()}>
        <div className="cm-trend-filters__field">
          <span>{t(`${COPY}.window`)}</span>
          <div className="cm-trend-filters__group" role="group" aria-label={t(`${COPY}.window`)}>
            {WINDOWS.map((windowDays) => (
              <button
                key={windowDays}
                type="button"
                className={windowDays === days ? "cm-admin-analytics__preset cm-admin-analytics__preset--active" : "cm-admin-analytics__preset"}
                aria-pressed={windowDays === days}
                onClick={() => setDays(windowDays)}
              >
                {t(`${COPY}.days`, { count: windowDays })}
              </button>
            ))}
          </div>
        </div>
        <label className="cm-trend-filters__field">
          <span>{t(`${COPY}.listingType`)}</span>
          <select value={listingType} onChange={(event) => setListingType(event.target.value)}>
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {type === "ALL" ? t(`${COPY}.allTypes`) : typeLabel(type)}
              </option>
            ))}
          </select>
        </label>
        <CasaMapaAdminFilterCombobox
          label={t(`${COPY}.city`)}
          options={cityOptions}
          multiple
          values={cities}
          onChange={setCities}
          allLabel={t(`${COPY}.allCities`)}
          placeholder={t(`${COPY}.citySearch`)}
          clearLabel={t(`${COPY}.cityClear`)}
          noResultsLabel={t(`${COPY}.cityNoResults`)}
          selectedCountLabel={(vars) => t(`${COPY}.citySelectedCount`, vars)}
        />
      </form>

      {error ? <p className="form-error">{error}</p> : null}
      {loading && !report ? <p className="muted">{t("realEstate.casaMapa.admin.loading")}</p> : null}
      {loading && report ? <p className="muted">{t(`${COPY}.updating`)}</p> : null}

      {report ? (
        <>
          <div className="cm-admin-analytics__metric-grid">
            <Metric
              label={t(`${COPY}.uniqueProperties`)}
              value={count(summary.uniqueProperties)}
              hint={t(`${COPY}.uniqueHint`)}
              delta={summary.deltas?.uniqueProperties}
              tone="blue"
            />
            <Metric
              label={t(`${COPY}.availableNow`)}
              value={count(summary.availableUniqueProperties)}
              hint={t(`${COPY}.availableHint`, { active: count(summary.activeUniqueProperties) })}
            />
            <Metric
              label={t(`${COPY}.newProperties`)}
              value={count(summary.created)}
              hint={t(`${COPY}.vsPrior`)}
              delta={summary.deltas?.created}
            />
            <Metric
              label={t(`${COPY}.priceIncreases`)}
              value={count(summary.priceIncreases)}
              hint={t(`${COPY}.vsPrior`)}
              delta={summary.deltas?.priceIncreases}
              tone="teal"
            />
            <Metric
              label={t(`${COPY}.priceDecreases`)}
              value={count(summary.priceDecreases)}
              hint={t(`${COPY}.vsPrior`)}
              delta={summary.deltas?.priceDecreases}
            />
            <Metric
              label={t(`${COPY}.becameUnavailable`)}
              value={count(summary.becameUnavailable)}
              hint={t(`${COPY}.vsPrior`)}
              delta={summary.deltas?.becameUnavailable}
            />
          </div>

          <div className="cm-admin-analytics__split">
            <CollapsiblePanel title={t(`${COPY}.weeklyTitle`)} hint={t(`${COPY}.weeklyHint`)}>
              <WeeklyMovementChart weeks={report.weekly} locale={locale} t={t} />
            </CollapsiblePanel>
            <CollapsiblePanel title={t(`${COPY}.priceHistoryTitle`)} hint={t(`${COPY}.priceHistoryHint`)}>
              <PriceHistoryChart
                points={report.priceHistory}
                locale={locale}
                formatMoney={formatMoney}
                t={t}
              />
            </CollapsiblePanel>
          </div>

          <CollapsiblePanel title={t(`${COPY}.citiesTitle`)} hint={t(`${COPY}.citiesHint`)}>
            <div className="casamapa-admin-table-wrap">
              <table className="casamapa-admin-table">
                <thead>
                  <tr>
                    {cityColumns.map((column) => (
                      <SortableHeader
                        key={column.key}
                        column={column}
                        sort={citySort}
                        onSort={setCitySort}
                        t={t}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cityRows.length ? (
                    cityRows.map((row) => (
                      <tr
                        key={`${row.city}-${row.listingType}`}
                        className={cities.includes(row.city) ? "cm-trend-row--active" : undefined}
                      >
                        <td>{row.city}</td>
                        <td>{typeLabel(row.listingType)}</td>
                        <td>{count(row.properties)}</td>
                        <td>{money(row.medianPrice)}</td>
                        <td>{area(row.medianAreaM2)}</td>
                        <td>{money(row.medianPricePerM2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6}>{t(`${COPY}.emptyCities`)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CollapsiblePanel>

          <CollapsiblePanel title={t(`${COPY}.recentTitle`)} hint={t(`${COPY}.recentHint`)}>
            <div className="casamapa-admin-table-wrap">
              <table className="casamapa-admin-table">
                <thead>
                  <tr>
                    {recentColumns.map((column) => (
                      <SortableHeader
                        key={column.key}
                        column={column}
                        sort={recentSort}
                        onSort={setRecentSort}
                        t={t}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentRows.length ? (
                    recentRows.map((row) => {
                      const up = row.changeType === "PRICE_INCREASED" || (row.changePercent != null && row.changePercent > 0);
                      const down = row.changeType === "PRICE_DECREASED" || (row.changePercent != null && row.changePercent < 0);
                      return (
                        <tr
                          key={`${row.canonicalListingId}-${row.changedAt}-${row.changeType}`}
                          className={`cm-trend-row${selectedId === row.canonicalListingId ? " cm-trend-row--active" : ""}`}
                          onClick={() => openPropertyModal(row)}
                        >
                          <td>{dateLabel(row.changedAt)}</td>
                          <td>
                            <button
                              type="button"
                              className="cm-trend-property-button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openPropertyModal(row);
                              }}
                            >
                              <strong>{row.title || t(`${COPY}.untitled`)}</strong>
                              <span className="muted">{placeLabel(row)}</span>
                            </button>
                          </td>
                          <td>
                            <span className={`cm-trend-delta cm-trend-delta--${up ? "up" : down ? "down" : "flat"}`}>
                              {up ? "↑" : down ? "↓" : "→"} {typeLabel(row.listingType)}
                            </span>
                          </td>
                          <td>
                            {money(row.previousPrice)} → {money(row.newPrice)}
                            {row.changePercent != null ? (
                              <div className={`cm-trend-delta cm-trend-delta--${row.changePercent > 0 ? "up" : row.changePercent < 0 ? "down" : "flat"}`}>
                                {row.changePercent > 0 ? "+" : ""}
                                {row.changePercent.toFixed(1)}%
                              </div>
                            ) : null}
                          </td>
                          <td>{count(row.activeSourceCount)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5}>{t(`${COPY}.emptyPrices`)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CollapsiblePanel>
          {selectedRow ? (
            <PropertyTrendModal
              property={selectedRow}
              points={report.selectedPriceHistory?.points || []}
              loading={historyLoading && report.selectedPriceHistory?.canonicalListingId !== selectedId}
              onClose={closePropertyModal}
              t={t}
              typeLabel={typeLabel}
              money={money}
              dateLabel={dateLabel}
              formatMoney={formatMoney}
              adminPath={
                selectedRow.canonicalListingId
                  ? path(`listings/${selectedRow.canonicalListingId}`)
                  : null
              }
              publicPath={selectedRow.publicUrl || null}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
